/**
 * The trivia card a checked-in donor plays from (GH-912).
 *
 * ## Self-contained, like `ReactionBar`
 *
 * `TriviaGate` renders this once the donor is checked in with a session (`here && token`), and
 * owns the way in for everyone else — the join-checks-you-in state and the signed-out teaser —
 * so a trivia round that is the only live thing is still reachable. From here on everything is
 * this component's own: its own poll, its own join/answer calls, its own states. That mirrors
 * how `PromptCard` owns its expanded body rather than `EventPage` reaching into it, except this
 * card has no row to tap open — there is nothing to collapse once a round is live, so the full
 * state renders directly under the row. One addition from the gate: a nickname parked before
 * check-in (`trivia-join-intent.ts`) completes its join here, once, on mount.
 *
 * ## Two poll speeds, not one
 *
 * `PROMPT_POLL_MS` (fifteen seconds, in `EventPage`) is right for "did the organizer open a new
 * poll" — nobody needs to notice that inside a second. It is wrong for "has everyone answered
 * yet" and "how much time is left on this question", which is what a donor is actually staring at
 * once a question is live. So this card polls slower while nothing is happening (`LOBBY_POLL_MS`,
 * matching `PROMPT_POLL_MS`'s own cadence) and faster while a question is live and counting down
 * (`ACTIVE_POLL_MS`), so the countdown and the "locked in, waiting for the others" state both feel
 * current without turning a full hall's phones into a two-second request storm.
 *
 * GH-925: the countdown ticks each second off the server's `phaseEndsAt` (a one-second clock,
 * `nowMs`), and `between_questions` polls at the live cadence. The card also schedules one read
 * at `phaseEndsAt` plus a little jitter — the phone KNOWS when the phase flips, so it fetches
 * then rather than waiting out an interval tick. The number is decoration: it must never gate
 * whether a tap is allowed, which is the server's decision alone, made from its own receipt
 * time, not this card's clock.
 *
 * ## No early correctness (GH-925)
 *
 * The submit response only acknowledges. A locked-in answer is shown as the donor's own
 * highlighted selection; right/wrong, points and the correct answer appear only when the round
 * poll reaches `between_questions` (`myResult`, `reveal`).
 *
 * ## The optimistic lock
 *
 * A tap sets `answeredQuestionId` to the question just answered *before* the network call
 * resolves. The server already refuses a second answer with `conflict`, but a UI that only
 * disables once the round trip returns lets an impatient double-tap fire twice before the first
 * response lands — this is defense the server does not need but a thumb does.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import type { TriviaCorrectAnswerWire } from "@givehere/contracts";
import {
  DonorApiError,
  fetchDonorSession,
  fetchTriviaRound,
  joinTriviaRound,
  submitTriviaAnswer,
  type TriviaDonorRound,
} from "./donor-session.js";
import { EventActionCard } from "./EventActionCard.js";
import { TriviaIcon } from "./icons.js";
import { clearTriviaJoin, parkedTriviaJoin } from "./trivia-join-intent.js";
import { readRevealFacts, type TriviaDonorQuestion } from "./trivia-wire.js";

/** While nothing is live yet: joining and the lobby. */
export const LOBBY_POLL_MS = 15000;
/** Whenever a round is in play (GH-925: between questions too, so the next question lands on time). */
const ACTIVE_POLL_MS = 2500;
/** How long after a phase deadline the boundary read fires, for the server's lazy transition. */
const BOUNDARY_JITTER_MS = 300;
/** The countdown's clock. */
const TICK_MS = 1000;

export function TriviaCard({ eventId, token }: { eventId: string; token: string }) {
  const [data, setData] = useState<TriviaDonorRound | null>(null);
  const [nickname, setNickname] = useState("");
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /** The question this card has already sent an answer for — the optimistic lock. */
  const [answeredQuestionId, setAnsweredQuestionId] = useState<string | null>(null);
  /** What this card just sent for `answeredQuestionId` — the optimistic "locked in" highlight. */
  const [submittedAnswer, setSubmittedAnswer] = useState<TriviaCorrectAnswerWire | null>(null);
  /** The jumble builder: choice indices tapped so far, in the order the player built them. */
  const [order, setOrder] = useState<readonly number[]>([]);
  /** The countdown's clock, ticking each second. */
  const [nowMs, setNowMs] = useState(() => Date.now());

  const round = data?.round ?? null;
  const currentQuestion = round?.currentQuestion ?? null;
  const myPlayerId = data?.myPlayerId ?? null;
  const myScore = data?.myScore ?? null;
  const hasAnswered = data?.hasAnswered ?? null;
  /* This donor's selection for the question on screen: the optimistic one from this card, else
     the server's echo (a reload, or another device answered). Never carries correctness. */
  const serverAnswer = data?.myAnswer ?? null;
  const mySelection: TriviaCorrectAnswerWire | null =
    currentQuestion === null
      ? null
      : answeredQuestionId === currentQuestion.id && submittedAnswer !== null
        ? submittedAnswer
        : serverAnswer !== null && serverAnswer.questionId === currentQuestion.id
          ? serverAnswer.submittedAnswer
          : null;
  const myResult =
    data?.myResult && data.myResult.questionId === currentQuestion?.id ? data.myResult : null;
  const phaseEndsAtMs = round?.phaseEndsAt ? Date.parse(round.phaseEndsAt) : null;

  /*
    A reasonable default nickname, best-effort. A join parked before check-in
    (`trivia-join-intent.ts`) wins — it is the name the donor just typed — and otherwise this
    reads its own copy of the session, since `TriviaCard` takes only `{eventId, token}` and
    `EventPage` does not thread the session through. A failed read just leaves the field blank;
    nobody is blocked from playing because their name could not be looked up.
  */
  useEffect(() => {
    const parked = parkedTriviaJoin(eventId);
    if (parked !== null && parked.trim().length > 0) {
      setNickname(parked);
      return;
    }
    let live = true;
    fetchDonorSession(token).then(
      (session) => {
        if (live && session.profile.displayName) setNickname(session.profile.displayName);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [token, eventId]);

  const refresh = useCallback(
    () =>
      fetchTriviaRound(token, eventId).then(
        (value) => setData(value),
        // A failed poll leaves the last good state on screen, same rule as the goal and
        // engagement polls on `EventPage`: a blip here must not blank a card that was working.
        () => undefined,
      ),
    [token, eventId],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    // Nothing left to ask for once the round is finished — the card has already shown the final
    // score, and polling further would just keep hitting a route with nothing new to say.
    if (round?.status === "finished") return;
    const inPlay = round?.status === "active" || round?.status === "between_questions";
    const ms = inPlay ? ACTIVE_POLL_MS : LOBBY_POLL_MS;
    const timer = setInterval(refresh, ms);
    return () => clearInterval(timer);
  }, [refresh, round?.status]);

  /* The boundary read (GH-925): one fetch at the phase deadline plus jitter, so the phone does
     not wait out an interval tick to learn the phase flipped. The server stays the authority on
     the transition — this only decides when to look. A deadline already behind us is skipped
     (the interval above covers it), so a phone clock running ahead cannot spin this. */
  useEffect(() => {
    if (phaseEndsAtMs === null || round?.status === "finished") return;
    const wait = phaseEndsAtMs + BOUNDARY_JITTER_MS - Date.now();
    if (wait <= 0) return;
    const timer = setTimeout(refresh, wait);
    return () => clearTimeout(timer);
  }, [refresh, phaseEndsAtMs, round?.status]);

  /* The countdown's one-second clock, only while a question is on screen to count down. */
  const counting = round?.status === "active" && currentQuestion !== null;
  useEffect(() => {
    if (!counting) return;
    setNowMs(Date.now());
    const timer = setInterval(() => setNowMs(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [counting]);

  /* The next question (or no question at all) clears the previous one's lock and its selection —
     this is what lets the between-questions and next-question states read correctly without the
     server telling this card explicitly "that one is over now". */
  useEffect(() => {
    setAnsweredQuestionId(null);
    setSubmittedAnswer(null);
    setOrder([]);
  }, [currentQuestion?.id]);

  /*
    The join parked before check-in (`trivia-join-intent.ts`), completed here exactly once.

    Taking part is what checks a donor in: the pre-check-in join state (`TriviaGate`) collects
    the nickname, performs the page's normal check-in — possibly via the verify-your-mobile
    journey at `/c/<eventId>` — and this card, mounting checked-in, finishes what was asked for.
    The intent is spent whichever way it lands: a refusal shows the server's own words and leaves
    the parked name in the field for a plain retry, never a loop.
  */
  const autoJoinAttempted = useRef(false);
  useEffect(() => {
    if (autoJoinAttempted.current || data === null) return;
    const parked = parkedTriviaJoin(eventId);
    if (parked === null || parked.trim().length === 0) return;
    if (data.myPlayerId !== null || data.round === null || data.round.status === "finished") {
      // Already in, or nothing left to join: the intent is stale and must not fire later.
      clearTriviaJoin(eventId);
      return;
    }
    autoJoinAttempted.current = true;
    setJoining(true);
    setJoinError(null);
    joinTriviaRound(token, eventId, parked.trim())
      .then(() => {
        clearTriviaJoin(eventId);
        return refresh();
      })
      .catch((caught: unknown) => {
        clearTriviaJoin(eventId);
        setJoinError(caught instanceof DonorApiError ? caught.message : "Could not join trivia.");
      })
      .finally(() => setJoining(false));
  }, [data, eventId, token, refresh]);

  if (round === null) {
    // No round at all, nothing fetched yet, or the poll is failing: there is nothing to say, same
    // "absence is not an error" rule `prompts` being empty already follows on this page.
    return null;
  }

  const handleJoin = (event: FormEvent) => {
    event.preventDefault();
    const typed = nickname.trim();
    if (typed.length === 0) return;
    setJoining(true);
    setJoinError(null);
    joinTriviaRound(token, eventId, typed)
      .then(() => refresh())
      .catch((caught: unknown) =>
        setJoinError(caught instanceof DonorApiError ? caught.message : "Could not join trivia."),
      )
      .finally(() => setJoining(false));
  };

  const submit = (questionId: string, chosen: TriviaCorrectAnswerWire) => {
    setAnsweredQuestionId(questionId);
    setSubmittedAnswer(chosen);
    setSubmitting(true);
    setSubmitError(null);
    submitTriviaAnswer(token, round.id, questionId, chosen)
      // GH-925: an acknowledgement only; whether it was right waits for the reveal poll.
      .then((ack) => setSubmittedAnswer(ack.submittedAnswer))
      .catch((caught: unknown) => {
        setSubmitError(
          caught instanceof DonorApiError ? caught.message : "Could not send that answer.",
        );
        // Nothing was recorded, so the lock and the highlight must not stand; the poll re-locks
        // if the server did take an answer from somewhere else.
        setAnsweredQuestionId(null);
        setSubmittedAnswer(null);
        setOrder([]);
        void refresh();
      })
      .finally(() => setSubmitting(false));
  };

  const pickJumbleIndex = (questionId: string, index: number, total: number) => {
    if (order.includes(index)) return;
    const next = [...order, index];
    setOrder(next);
    if (next.length === total) submit(questionId, { order: next });
  };

  const locked = answeredQuestionId === currentQuestion?.id || hasAnswered === true;
  /*
    A status this build does not know — the autoplay reveal/leaderboard work in flight adds
    phases — renders as the quiet between-questions state: the donor's own score and a waiting
    line. The spectacle (leaderboard, shoutouts) belongs to the big screen, so the graceful
    degradation and the intended rendering are the same thing here.
  */
  const betweenQuestionsLike =
    round.status === "between_questions" || !KNOWN_ROUND_STATUSES.has(round.status);
  /* End-of-question reveal facts, when the server starts sending them — see `trivia-wire.ts`. */
  const reveal = betweenQuestionsLike ? readRevealFacts(round) : null;

  return (
    <EventActionCard
      glyph={<TriviaIcon />}
      label="Trivia"
      meta={roundMeta(round.status)}
      live={round.status === "active"}
      failure={joinError ?? submitError}
    >
      {myPlayerId === null && (
        <form className="trivia__join" onSubmit={handleJoin}>
          <label className="field">
            <span>Your name for the leaderboard</span>
            <input
              maxLength={40}
              value={nickname}
              onChange={(changed) => setNickname(changed.target.value)}
            />
          </label>
          <button type="submit" className="pay" disabled={joining || nickname.trim().length === 0}>
            {joining ? "Joining…" : "Join trivia"}
          </button>
        </form>
      )}

      {myPlayerId !== null && round.status === "lobby" && (
        <p className="notice">You&rsquo;re in — waiting for the host to start.</p>
      )}

      {myPlayerId !== null && round.status === "active" && currentQuestion !== null && (
        <>
          <TriviaQuestion
            question={currentQuestion}
            deadlineMs={phaseEndsAtMs}
            startedAt={round.questionStartedAt}
            nowMs={nowMs}
            order={order}
            submitting={submitting}
            locked={locked}
            selection={mySelection}
            onChoose={(choiceIndex) => submit(currentQuestion.id, { choiceIndex })}
            onPickJumbleIndex={(index) =>
              pickJumbleIndex(currentQuestion.id, index, currentQuestion.choices.length)
            }
          />
          {locked && <p className="notice">Answer locked in — results when time is up.</p>}
        </>
      )}

      {myPlayerId !== null && betweenQuestionsLike && myResult !== null && (
        <p className={myResult.isCorrect ? "notice" : "notice notice--error"}>
          <strong>{myResult.isCorrect ? "Correct!" : "Not quite."}</strong>{" "}
          {myResult.isCorrect && `+${myResult.pointsAwarded} points.`}
        </p>
      )}

      {myPlayerId !== null && betweenQuestionsLike && (
        <p className="notice">
          <strong>{myScore ?? 0} points so far</strong>
          <br />
          {reveal !== null && reveal.correctAnswerLabel !== null && (
            <>
              The right answer: {reveal.correctAnswerLabel}
              <br />
            </>
          )}
          Waiting for the next question…
        </p>
      )}

      {myPlayerId !== null && round.status === "finished" && (
        <p className="notice">
          <strong>{myScore ?? 0} points</strong> — thanks for playing!
        </p>
      )}
    </EventActionCard>
  );
}

/** The phases this build knows; anything newer renders as the quiet between-questions state. */
const KNOWN_ROUND_STATUSES: ReadonlySet<string> = new Set([
  "lobby",
  "active",
  "between_questions",
  "finished",
]);

/** The row's own quick status word, next to the glyph. The body underneath carries the score. */
function roundMeta(status: string): string {
  if (status === "active") return "Live now";
  if (status === "finished") return "Finished";
  if (status === "lobby") return "Lobby";
  // "between_questions", and any phase this build has not met — which, in the autoplay flow,
  // is by definition a moment between one question and the next.
  return "Between questions";
}

/**
 * One live question: the prompt, a visual countdown (see the file header), and the choices.
 * Multiple-choice submits on the first tap; jumble builds `order` one tap at a time.
 */
function TriviaQuestion({
  question,
  deadlineMs,
  startedAt,
  nowMs,
  order,
  submitting,
  locked,
  selection,
  onChoose,
  onPickJumbleIndex,
}: {
  question: TriviaDonorQuestion;
  /** The server's phase deadline (`phaseEndsAt`), epoch ms; falls back to start + limit. */
  deadlineMs: number | null;
  startedAt: string | null;
  /** The card's one-second clock — the countdown is derived from it, never client-authoritative. */
  nowMs: number;
  order: readonly number[];
  submitting: boolean;
  /** The donor's answer is in: choices stop taking taps and their selection is highlighted. */
  locked: boolean;
  selection: TriviaCorrectAnswerWire | null;
  onChoose: (choiceIndex: number) => void;
  onPickJumbleIndex: (index: number) => void;
}) {
  // Both facts are tolerated as absent (`trivia-wire.ts`): no time limit means no countdown,
  // and zero points means the line says nothing rather than "0 points".
  const endMs =
    deadlineMs ??
    (startedAt && question.timeLimitSeconds !== null
      ? Date.parse(startedAt) + question.timeLimitSeconds * 1000
      : null);
  const remainingSeconds = endMs === null ? null : Math.max(0, Math.ceil((endMs - nowMs) / 1000));
  const pickedIndices: readonly number[] =
    selection === null
      ? []
      : "choiceIndex" in selection
        ? [selection.choiceIndex]
        : selection.order;
  const shownOrder = locked ? pickedIndices : order;

  return (
    <div className="trivia__question">
      <p className="trivia__prompt" dir="auto">
        {question.prompt}
      </p>
      {(question.points > 0 || remainingSeconds !== null) && (
        <p className="trivia__timer notice">
          {question.points > 0 && (
            <>
              {question.points} {question.points === 1 ? "point" : "points"}
            </>
          )}
          {question.points > 0 && remainingSeconds !== null && <> · </>}
          {remainingSeconds !== null && <>{remainingSeconds}s left</>}
        </p>
      )}

      {question.questionType === "multiple_choice" && (
        <div className="donor__choices">
          {question.choices.map((choice, index) => (
            <button
              key={index}
              type="button"
              className={pickedIndices.includes(index) ? "method method--picked" : "method"}
              disabled={submitting || locked}
              aria-pressed={pickedIndices.includes(index)}
              onClick={() => onChoose(index)}
            >
              {choice}
              {locked && pickedIndices.includes(index) && " — locked in"}
            </button>
          ))}
        </div>
      )}

      {question.questionType === "jumble" && (
        <>
          <p className="notice">Tap the choices in what you think is the right order.</p>
          {shownOrder.length > 0 && (
            <p className="trivia__order">
              {locked ? "Your order: " : "Your order so far: "}
              {shownOrder.map((index) => question.choices[index]).join(" → ")}
            </p>
          )}
          <div className="donor__choices">
            {question.choices.map((choice, index) => (
              <button
                key={index}
                type="button"
                className={shownOrder.includes(index) ? "method method--picked" : "method"}
                disabled={submitting || locked || order.includes(index)}
                onClick={() => onPickJumbleIndex(index)}
              >
                {choice}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
